'use client';

import { useTranslation } from 'react-i18next';

import {
  recordContext,
  type RecordTerminologyToken,
  type RecordType,
} from '../lib/record-terminology';

/**
 * Client hook that resolves `record.*` terminology tokens for a given record
 * type, applying the i18next `context` fork ONCE so callers never pass it.
 *
 * Client-side counterpart to `recordTerminologyKey` (which server components
 * hand to `<Trans>`). The returned `t` accepts a bare token (`'publish'`,
 * `'applications'`, …) plus optional interpolation values and resolves
 * `auditions:record.<token>` with `{ context }` baked in — so the caller
 * decides the record type once and gets the right wording everywhere.
 *
 *   const { t } = useRecordTerminology(recordTypeOf(audition));
 *   t('publish');        // "Publish audition" | "Publish competition"
 *   t('applications');   // "Applications"      | "Entries"
 *
 * Lives under hooks/ (NOT lib/) deliberately: react-i18next calls
 * `createContext` at module evaluation, so any module graph that reaches it
 * must be client-side. The lib barrel is consumed by server-only API routes
 * (e.g. /api/competitions/[slug]/booklet/scholarship imports the
 * scholarship-export utils from `@kit/auditions/lib`) — exporting this hook
 * there broke `next build` with "createContext is not a function" during
 * page-data collection (Vercel failure on PR #1163, 2026-06-04).
 */
export function useRecordTerminology(type: RecordType): {
  t: (
    token: RecordTerminologyToken,
    values?: Record<string, unknown>,
  ) => string;
} {
  const { t } = useTranslation('auditions');
  const context = recordContext(type);

  return {
    t: (token, values) =>
      t(`record.${token}`, { context, ...values }) as string,
  };
}
